import Skeleton from "../../../Components/Common/Skeleton/Skeleton";
import { SettingsRow } from "../types/notification.types";

interface NotificationSettingsProps {
  rows: SettingsRow[];
  loading: boolean;
  channelLoading: Record<string, boolean>;
  onToggle: (id: string, channel: "email" | "sms") => void;
}

interface Category {
  title: string;
  match: (label: string) => boolean;
}

const CATEGORIES: Category[] = [
  {
    title: "Draw & Disbursement",
    match: (l) => /draw|disburs|aia|sign/i.test(l),
  },
  {
    title: "Inspections & Reports",
    match: (l) => /inspect|report|overbill|progress/i.test(l),
  },
  { title: "Lien Waivers", match: (l) => /lien|waiver/i.test(l) },
  { title: "Change Orders", match: (l) => /change.?order/i.test(l) },
];

function groupRows(
  rows: SettingsRow[],
): { title: string; rows: SettingsRow[] }[] {
  const matched = new Set<string>();
  const groups: { title: string; rows: SettingsRow[] }[] = [];

  for (const cat of CATEGORIES) {
    const catRows = rows.filter(
      (r) => !matched.has(r.id) && cat.match(r.label),
    );
    catRows.forEach((r) => matched.add(r.id));
    if (catRows.length > 0) groups.push({ title: cat.title, rows: catRows });
  }

  const remaining = rows.filter((r) => !matched.has(r.id));
  if (remaining.length > 0)
    groups.push({ title: "Project & Platform", rows: remaining });

  return groups;
}

export default function NotificationSettings({
  rows,
  loading,
  channelLoading,
  onToggle,
}: NotificationSettingsProps) {
  const groups = groupRows(rows);

  return (
    <div className="ns-wrap">
      <div className="ns-card">
        <div className="ns-card-title">Notification Types — Per Channel</div>

        <div className="ns-channel-header">
          <span className="ns-ch-label ns-ch-notif">Notification</span>
          <span className="ns-ch-label">In-App</span>
          <span className="ns-ch-label">Email</span>
          <span className="ns-ch-label">SMS</span>
        </div>
        {loading ? (
          <>
            <div className="ns-nr-info p-2">
              <Skeleton width="55%" height={15} borderRadius={4} />
            </div>
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="ns-row ns-row-skeleton" key={i}>
                <div className="ns-nr-info">
                  <Skeleton width="55%" height={33} borderRadius={4} />
                  <Skeleton
                    width="40%"
                    height={10}
                    borderRadius={4}
                    style={{ marginTop: 4 }}
                  />
                </div>

                <div className="ns-nr-toggle">
                  <Skeleton width={28} height={28} borderRadius={4} />
                </div>

                <div className="ns-nr-toggle">
                  <Skeleton width={28} height={28} borderRadius={4} />
                </div>

                <div className="ns-nr-toggle">
                  <Skeleton width={28} height={28} borderRadius={4} />
                </div>
              </div>
            ))}
          </>
        ) : (
          groups.map(({ title, rows: catRows }) => (
            <div className="ns-category" key={title}>
              <div className="ns-cat-title">{title}</div>
              {catRows.map((row) => (
                <div className="ns-row" key={row.id}>
                  <div className="ns-nr-info">
                    <div className="ns-nr-label">{row.label}</div>
                    {row.description && (
                      <div className="ns-nr-sub">{row.description}</div>
                    )}
                  </div>
                  <div className="ns-nr-toggle">
                    <div
                      className="ns-check on ns-check-inapp"
                      title="In-app notifications cannot be disabled"
                    >
                      ✓
                    </div>
                  </div>
                  <div className="ns-nr-toggle">
                    {row.defaultChannels?.email !== false ? (
                      <div
                        className={`ns-check${row.email ? " on" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggle(row.id, "email")}
                        onKeyDown={(e) =>
                          e.key === "Enter" && onToggle(row.id, "email")
                        }
                      >
                        {channelLoading[`${row.id}-email`] ? (
                          <span className="btn-spinner" />
                        ) : row.email ? (
                          "✓"
                        ) : (
                          ""
                        )}
                      </div>
                    ) : (
                      <div
                        className={`ns-check${row.email ? " on" : ""} ns-check-disabled`}
                        style={{ cursor: "not-allowed" }}
                        title="Email not available for this notification"
                      />
                    )}
                  </div>
                  <div className="ns-nr-toggle">
                    {row.defaultChannels?.sms !== false ? (
                      <div
                        className={`ns-check${row.sms ? " on" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggle(row.id, "sms")}
                        onKeyDown={(e) =>
                          e.key === "Enter" && onToggle(row.id, "sms")
                        }
                      >
                        {channelLoading[`${row.id}-sms`] ? (
                          <span className="btn-spinner" />
                        ) : row.sms ? (
                          "✓"
                        ) : (
                          ""
                        )}
                      </div>
                    ) : (
                      <div
                        className={`ns-check${row.sms ? " on" : ""} ns-check-disabled`}
                        style={{ cursor: "not-allowed" }}
                        title="SMS not available for this notification"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="ns-footer">
        In-app notifications cannot be disabled — they are required for platform
        functionality. SMS notifications require a verified phone number. Email
        is the default channel for all alerts. You can unsubscribe from
        individual types above without affecting other channels.
      </div>
    </div>
  );
}
